import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Globe, HelpCircle, Shield, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AppFooter = () => {
  const [appSettings, setAppSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/app-settings`);
        if (res.ok) {
          const data = await res.json();
          setAppSettings(data);
        }
      } catch (err) {
      }
    };
    fetchSettings();
  }, []);

  if (!appSettings) return null;

  const hasContactInfo = appSettings.contact_email || appSettings.contact_phone || appSettings.contact_address;
  const hasLinks = appSettings.website_url || appSettings.support_url || appSettings.privacy_url || appSettings.terms_url;

  if (!hasContactInfo && !hasLinks) return null;

  return (
    <footer className="bg-stone-800 text-stone-300 pt-8 pb-24 lg:pb-8 mt-auto lg:ml-56 overflow-hidden" data-testid="app-footer">
      <div className="px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              {appSettings.app_logo ? (
                <img
                  src={`${BACKEND_URL}/api/uploads/${appSettings.app_logo}`}
                  alt={appSettings.app_name}
                  className="w-10 h-10 rounded-lg object-contain bg-white p-1"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                  <span className="text-xl">🐝</span>
                </div>
              )}
              <div>
                <h3 className="font-bold text-white text-lg">{appSettings.app_name || 'Thrive'}</h3>
                <p className="text-xs text-stone-400">{appSettings.app_tagline || 'Beekeeping Management'}</p>
              </div>
            </div>
            {appSettings.app_description && (
              <p className="text-sm text-stone-400 mb-4">{appSettings.app_description}</p>
            )}
          </div>

          {/* Contact Info */}
          {hasContactInfo && (
            <div>
              <h4 className="font-semibold text-white mb-4">Contact Us</h4>
              <div className="space-y-3">
                {appSettings.contact_email && (
                  <a 
                    href={`mailto:${appSettings.contact_email}`}
                    className="flex items-center gap-2 text-sm hover:text-amber-400 transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    {appSettings.contact_email}
                  </a>
                )}
                {appSettings.contact_phone && (
                  <a 
                    href={`tel:${appSettings.contact_phone}`}
                    className="flex items-center gap-2 text-sm hover:text-amber-400 transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    {appSettings.contact_phone}
                  </a>
                )}
                {appSettings.contact_address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{appSettings.contact_address}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Links */}
          {hasLinks && (
            <div>
              <h4 className="font-semibold text-white mb-4">Links</h4>
              <div className="space-y-3">
                {appSettings.website_url && (
                  <a 
                    href={appSettings.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm hover:text-amber-400 transition-colors"
                  >
                    <Globe className="h-4 w-4" />
                    Website
                  </a>
                )}
                {appSettings.support_url && (
                  <a 
                    href={appSettings.support_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm hover:text-amber-400 transition-colors"
                  >
                    <HelpCircle className="h-4 w-4" />
                    Help & Support
                  </a>
                )}
                {appSettings.privacy_url && (
                  <a 
                    href={appSettings.privacy_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm hover:text-amber-400 transition-colors"
                  >
                    <Shield className="h-4 w-4" />
                    Privacy Policy
                  </a>
                )}
                {appSettings.terms_url && (
                  <a 
                    href={appSettings.terms_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm hover:text-amber-400 transition-colors"
                  >
                    <FileText className="h-4 w-4" />
                    Terms of Service
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Copyright */}
        <div className="border-t border-stone-700 mt-8 pt-6 text-center text-xs text-stone-500">
          <p>© {new Date().getFullYear()} {appSettings.app_name || 'Thrive'}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default AppFooter;
